import React from "react";
import { PageProps, graphql } from "gatsby";
import _ from "lodash";
import useSiteMetadata from "../hooks/use-site-metadata";
import useLinksetNav from "../hooks/use-linkset-nav";
import { useNavHierarchies } from "gatsby-jqi-graphql-helpers";
import { getImage, ImageDataLike } from "gatsby-plugin-image";
import HeroParagraph from "../wrappers/HeroParagraph";
import HTMLHead from "../components/HTMLHead";
import PageContentBody from "../components/PageContentBody";
import PageContentHeader from "../components/PageContentHeader"
import PageContentLayout from "../layouts/PageContentLayout";
import PageContentMain from "../components/PageContentMain";
import PageContentSidebar from "../components/PageContentSidebar";
import ParagraphWidget from "../wrappers/ParagraphWidget";
import SectionNav from "../components/SectionNav";
import SidebarNav from "../components/SidebarNav";
import TextHTMLParagraph from "../wrappers/TextHTMLParagraph";
import RelatedResearch from "../components/RelatedResearch";

import {
  filterRelatedArticles,
  filterRelatedEvents,
  filterRelatedPublications,
  getExternalGroups,
  filterPeople,
} from "../utilities/local-filters";
import ResearchArea from "../components/ResearchArea";

const BasicPage: React.FC<PageProps<JQI.BasicPageData, JQI.BasicPageContext>> = ({
  location,
  data,
  pageContext,
}) => {
  const siteMetadata = useSiteMetadata();
  const { page, researchAreaPublications, researchAreaArticles, researchAreaEvents, researchAreaPeople } = data;
  const { defaultNodesByPath = {}, groupId } = pageContext;
  const { breadcrumb, sidebar } = useNavHierarchies(
    page.fields.pagePath,
    defaultNodesByPath,
  );
  const linksetSidebar = (page.linkset && page.linkset[0]) ? useLinksetNav(page.fields.pagePath, page.linkset[0]) : null;
  const sortedSidebar = _.orderBy(sidebar.current, "title", "asc");
  const { hero, widgets, researchAreas, groups } = page.relationships;
  const researchArea = researchAreas?.name;

  // Filter and sort related publications based on research areas
  const pageResearchAreaPublications = !!researchAreas
    ? researchAreaPublications?.edges?.map(edge => edge.node) || []
    : [];
  const localRelatedPublications = filterRelatedPublications(pageResearchAreaPublications, groupId);

  // Filter and sort related articles based on research areas
  // const pageResearchAreasArticles = !!researchAreas
  //   ? researchAreaArticles?.edges?.map(edge => edge.node) || []
  //   : [];
  // const localRelatedArticles = filterRelatedArticles(pageResearchAreasArticles, groupId);
  // const sortedArticles = _.orderBy(localRelatedArticles, "created", 'desc');

  // // Filter related events based on research areas
  // const pageResearchAreaEvents = !!researchAreas
  //   ? researchAreaEvents?.edges?.map(edge => edge.node) || []
  //   : [];
  // const localRelatedEvents = filterRelatedEvents(pageResearchAreaEvents, groupId);
  // const sortedEvents = _.orderBy(localRelatedEvents, "field_event_date", 'asc');

  //  // Filter related people based on research areas
  // const pageResearchAreaPeople = !!researchAreas
  //   ? researchAreaPeople?.edges?.map(edge => edge.node) || []
  //   : [];
  // const localRelatedPeople = filterPeople(pageResearchAreaPeople, groupId);
  // const sortedPeople = _.orderBy(localRelatedPeople, "name", 'asc');

  const finalSidebar = linksetSidebar ? linksetSidebar : sidebar;
  const finalSortedSidebar = linksetSidebar ? linksetSidebar.current : sortedSidebar;
  const sectionNavItems = finalSidebar.current?.filter(({ url }: {url: string}) => url === location.pathname)?.[0]?.children;


  let externalGroups = groups ? getExternalGroups(groups, groupId) : [];
  externalGroups = getExternalGroups(externalGroups, groupId);


  // Extract imageUrl for use in og:image
  const metaImageData = hero?.relationships.heroImage?.relationships.mediaImage.localFile as ImageDataLike;
  const image = !!metaImageData ? getImage(metaImageData): null;
  const imageUrl = siteMetadata.siteUrl + image?.images.fallback?.src;

  // Extract description for use in meta
  const meta = page.metatag?.filter((tag) => tag.attributes.name === "description").map((tag) => (
    {
      description: tag.attributes.content,
    }
  )).pop();

  const sideNavHide = page.field_hide_sidebar || false;

  return (
    <>
      <HTMLHead
        title={page.title}
        description={page.body?.summary || meta?.description}
        path={page.fields.pagePath}
        openGraph={{
          article: {
            publishedTime: page.created,
            modifiedTime: page.changed,
          },
          image: {
            url: imageUrl || "",
          },
        }}
      />
      <PageContentLayout>
        {!sideNavHide && (
          <PageContentSidebar>
            <SidebarNav {...finalSidebar} menuLinks={finalSortedSidebar} />
          </PageContentSidebar>
        )}
        <PageContentMain>
          <PageContentHeader breadcrumb={breadcrumb} title={page.title} />
          <PageContentBody>
            <SectionNav items={sectionNavItems} />
            {!!hero && <HeroParagraph {...hero} />}
            {!!page.body?.processed && (
              <TextHTMLParagraph className="page-body" text={page.body} />
            )}
            {!!widgets.length &&
              widgets.map((widget, i) => (
                <ParagraphWidget
                  key={i}
                  label={widget.relationships?.paragraphType?.label || ""}
                  widget={widget}
                  groupId={siteMetadata.groupId}
                />
              ))}
            {!!researchArea &&
              <RelatedResearch
                articles={[]}
                publications={localRelatedPublications}
                people={[]}
                events={[]}
              />
            }
          </PageContentBody>
        </PageContentMain>
      </PageContentLayout>
    </>
  );
};

export default BasicPage;

export const query = graphql`
  query BasicPageQuery($id: String!, $researchAreas: [Int]) {
    page: nodePage(id: { eq: $id }) {
      ...BasicPage
      meta: field_meta {
        description
      }
    }
    researchAreaPublications: allPublication(
      filter: {
        fields: { pagePath: { ne: null } }
        field_publication_research_area: {elemMatch: {drupal_internal__target_id: {in: $researchAreas}}}
      }
      sort: [{ bibcite_year: DESC }, { title: ASC }]
    ) {
      edges {
        node {
          ...PublicationFeedItem
        }
      }
    }
    researchAreaPeople: allNodePerson(
      filter: {field_research_areas: {elemMatch: {drupal_internal__target_id: {in: $researchAreas}}}}
    ) {
      edges {
        node {
          ...PersonFeedItem
        }
      }
    }
    researchAreaEvents: allNodeEvent(
      filter: {field_research_areas: {elemMatch: {drupal_internal__target_id: {in: $researchAreas}}}}
    ) {
      edges {
        node {
          ...EventFeedItem
        }
      }
    }
    researchAreaArticles: allNodeArticle(
      filter: {field_research_areas: {elemMatch: {drupal_internal__target_id: {in: $researchAreas}}}}
    ) {
      edges {
        node {
          ...ArticleFeedItem
        }
      }
    }
  }
`;